/**
 * Audit diffing (FR-AUD-01, FR-AUD-02).
 *
 * The log covers **edits and deletes only** — creates are already covered by
 * `createdAt` and `enteredBy`, and logging them again would double the volume for no
 * information (FR-AUD-01).
 *
 * Pure: comparing two objects needs no database. The writer lives in `data/audit.ts`.
 */

export type AuditAction = 'update' | 'delete';

export interface AuditChange {
  field: string;
  before: unknown;
  after: unknown;
}

export interface AuditEntry {
  action: AuditAction;
  collection: string;
  documentId: string;
  changes: AuditChange[];
  context?: string | null;
}

/** Fields that carry no meaning in an audit trail and would drown the real changes. */
const NOISE = new Set(['updatedAt', 'createdAt', 'searchTokens']);

function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // null and undefined both mean "not set"; a field going from absent to null is
  // not a change anyone wants logged.
  if (a == null && b == null) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((value, i) => same(value, b[i]));
  }
  return false;
}

/**
 * Field-level before/after between two versions of a document.
 *
 * Only changed fields are returned. An "edit" that changed nothing produces an empty
 * list, and the caller should write no entry at all — an audit log full of no-op
 * entries is one nobody reads.
 */
export function diffFields(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): AuditChange[] {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const changes: AuditChange[] = [];

  for (const field of [...keys].sort()) {
    if (NOISE.has(field)) continue;
    if (same(before[field], after[field])) continue;
    changes.push({
      field,
      before: before[field] ?? null,
      after: after[field] ?? null,
    });
  }

  return changes;
}

/**
 * The whole document, as a delete entry's "before".
 *
 * A delete has no after-state, so the entry records what was lost. This matters because
 * audit entries **survive the record's purge** (FR-DEL-03): thirty days later the
 * transaction is gone and this is all that remains of it.
 */
export function deletionChanges(snapshot: Record<string, unknown>): AuditChange[] {
  return Object.keys(snapshot)
    .filter((field) => !NOISE.has(field))
    .sort()
    .map((field) => ({ field, before: snapshot[field] ?? null, after: null }));
}

export function describeChange(change: AuditChange): string {
  const show = (value: unknown) =>
    value === null || value === undefined
      ? '—'
      : Array.isArray(value)
        ? value.join(', ') || '—'
        : String(value);
  return `${change.field}: ${show(change.before)} → ${show(change.after)}`;
}
